import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  TextField,
  InputAdornment,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  // ListItemIcon,
  CircularProgress
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckIcon from "@mui/icons-material/Check";
import LanguageIcon from "@mui/icons-material/Language";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";

import { language_btn } from "../assets";
import ApiService, { ICountryData } from "../services/api";
import { useAppContext } from "../context/AppContext";
import { cookieHelpers } from "../utils/cookieUtils";
import { useIpLanguageDetection } from "../hooks/useIpLanguageDetection";



interface LanguageSelectorProps {
  onLanguageChange?: (sessionLanguage: string) => void;
  variant?: 'landing' | 'menu'; // 'landing' for landing page, 'menu' for menu slide-in
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({ onLanguageChange, variant = 'landing' }) => {
  const { setCountryData, flowType, updateLanguage, loadTranslations, currentLanguage, countryData: contextCountryData, isRTL, getTranslation, token, userId, sessionId } = useAppContext();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [searchTerm, setSearchTerm] = useState("");
  const [languages, setLanguages] = useState<ICountryData[]>(contextCountryData);
  const [loading, setLoading] = useState(contextCountryData.length === 0);
  const [isChangingLanguage, setIsChangingLanguage] = useState(false);
  const [selectedCode, setSelectedCode] = useState<string>("");
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const apiService = useRef(new ApiService());

  // Fetch country data from API and store all ICountryData values
  const fetchCountryData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.current.getLanguages(); 

      if (response.countryData && response.countryData.length > 0) {
        const uniqueLanguages = response.countryData.filter((country, index, self) => 
          index === self.findIndex(c => c.country_language_code === country.country_language_code)
        );
        

        const sortedLanguages = uniqueLanguages.sort((a, b) => {
          const nameA = a.country_language || '';
          const nameB = b.country_language || '';
          return nameA.localeCompare(nameB, 'en', { sensitivity: 'base' });
        });
        // Store all ICountryData values in languages state
        setLanguages(sortedLanguages);
        
        // Also store in global state so other components can access it
        setCountryData(sortedLanguages);
      } else {
        setLanguages([]);
        setCountryData([]);
      }
    } catch (error) {
      console.error('Failed to fetch country data:', error);
      setLanguages([]);
      setCountryData([]);
    } finally {
      setLoading(false);
    }
  }, [setCountryData]);

  useEffect(() => {
    if (contextCountryData.length > 0) {
      setLanguages(contextCountryData);
      setLoading(false);
      return;
    }
    void fetchCountryData();
  }, [fetchCountryData, contextCountryData]);


  useEffect(() => {
    const savedLanguage = cookieHelpers.getLanguage(flowType);

    // Treat "es" as no saved language to allow IP detection (same pattern as dev / hl-in-skin-fe with "en")
    if (savedLanguage && savedLanguage !== 'es') {
      setSelectedCode(savedLanguage);
      loadTranslations();
    } else {
      if (savedLanguage === 'es') {
        cookieHelpers.setLanguage('', flowType);
      }
      // Load Spanish translations immediately so the app has copy while IP detection runs
      loadTranslations('es');
    }
  }, [loadTranslations, flowType]);

  // Use IP-based language detection hook
  useIpLanguageDetection({
    languages,
    loading,
    flowType,
    onLanguageDetected: async (languageCode: string) => {
      updateLanguage(languageCode);
      await loadTranslations(languageCode);
    },
    onLanguageSet: (languageCode: string) => {
      setSelectedCode(languageCode);
    },
  });

  // Focus input when dropdown opens
  useEffect(() => {
    if (open && inputRef.current) {
      // Focus input since we're using custom dropdown
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 50);
    }
  }, [open]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        handleClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);



  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return languages;
    return languages.filter(
      country =>
        country.country_language?.toLowerCase().includes(term) ||
        country.country_language_code?.toLowerCase().includes(term)
    );
  }, [searchTerm, languages]);

  const selectedItem = useMemo(
    () => {
      const found = languages.find(country => country.country_language_code === selectedCode);
      return found || null;
    },
    [selectedCode, languages]
  );

  // Get current language from context if available
  const currentSelectedItem = useMemo(() => {
    const langCode = currentLanguage || selectedCode;
    return contextCountryData?.find(lang => lang.country_language_code === langCode) || selectedItem;
  }, [currentLanguage, selectedCode, contextCountryData, selectedItem]);

  const displayLabel = isChangingLanguage 
    ? "Changing Language..." 
    : (currentSelectedItem?.samcam_language || getTranslation("Txt1") || "Select Language");

  const openMenu = (anchor: HTMLElement) => {
    setAnchorEl(anchor);
    setSearchTerm("");
    // focus stays in the same TextField; user can type to filter
  };

  const handleTriggerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!open) {
      openMenu(e.currentTarget as HTMLElement);
      // Focus input immediately since we're not using Menu component
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 50);
    } else {
      // Close the menu if it's already open
      handleClose();
    }
  };

  const handleClose = async (country?: ICountryData) => {
    if (country && country.country_language_code) {
      try {
        setIsChangingLanguage(true);
        setSelectedCode(country.country_language_code);
        
        // Update language in context (handles cookies, direction, and document attributes)
        updateLanguage(country.country_language_code);
        
        // Use centralized loadTranslations function
        await loadTranslations(country.country_language_code);
        
        // Update language based on login status
        // If logged in: update user profile (don't update session)
        // If not logged in: update session only
        if (token && userId) {
          // User is logged in - update profile only
          try {
            const api = new ApiService(sessionId, token);
            await api.updateUserProfileDetails({
              userId: userId,
              language: country.country_language_code,
            });
          } catch (profileError) {
            // Log error but don't block language change
            console.error('Failed to update user profile language:', profileError);
          }
        } else if (sessionId) {
          // User is not logged in - update session only
          try {
            const api = new ApiService(sessionId);
            await api.updateUserSession({
              language: country.country_language_code,
            });
          } catch (sessionError) {
            // Log error but don't block language change
            console.error('Failed to update session language:', sessionError);
          }
        }
        
        onLanguageChange?.(country.country_language_code);
      } catch (error) {
        console.error('Failed to fetch translations for language:', country.country_language_code, error);
        // Update language even if translation API fails
        updateLanguage(country.country_language_code);
        onLanguageChange?.(country.country_language_code);
      } finally {
        setIsChangingLanguage(false);
      }
    }
    
    setAnchorEl(null);
    setSearchTerm("");
    inputRef.current?.blur();
  };

  // Shared dropdown content
  const renderDropdown = () => (
    <Paper
      sx={{
        position: 'absolute',
        top: variant === 'menu' ? '100%' : '100%',
        [variant === 'menu' ? 'left' : 'right']: 0,
        zIndex: 1300,
        maxHeight: variant === 'menu' 
          ? { xs: 'calc(100dvh - 120px)', sm: 'calc(100vh - 140px)', md: 'calc(100vh - 160px)' }
          : { xs: 300, sm: 400, md: 500 },
        height: variant === 'menu' ? 'auto' : undefined,
        overflowY: "auto",
        overflowX: "hidden",
        minWidth: variant === 'menu' ? '100%' : { xs: 220, sm: 260, md: 300 },
        maxWidth: variant === 'menu' ? '100%' : { xs: "90vw", sm: "80vw", md: "420px" },
        borderRadius: variant === 'menu' ? 0 : 2,
        boxShadow: variant === 'menu' ? "none" : "0 4px 20px rgba(0,0,0,0.15)",
        mt: variant === 'menu' ? 0 : 1,
        borderTop: variant === 'menu' ? "1px solid var(--color-border)" : "none",
      }}
    >
      {/* Search field for menu variant */}
      {variant === 'menu' && open && (
        <Box sx={{ p: 2, borderBottom: "1px solid var(--color-border)", direction: isRTL ? 'rtl' : 'ltr' }}>
          <TextField
            inputRef={inputRef}
            placeholder={getTranslation("Txt958",) || "Search languages"}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            fullWidth
            size="small"
            InputProps={{
              [isRTL ? "endAdornment" : "startAdornment"]: (
                <InputAdornment position={isRTL ? "end" : "start"}>
                  <SearchIcon sx={{ color: "var(--color-text-secondary)", fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            inputProps={{
              dir: isRTL ? 'rtl' : 'ltr',
              style: {
                textAlign: isRTL ? 'right' : 'left',
                direction: isRTL ? 'rtl' : 'ltr',
              },
            }}
            sx={{
              direction: isRTL ? 'rtl' : 'ltr',
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                direction: isRTL ? 'rtl' : 'ltr',
                '& fieldset': {
                  borderColor: 'var(--color-border)',
                },
              },
              '& .MuiInputBase-input': {
                textAlign: isRTL ? 'right' : 'left',
                direction: isRTL ? 'rtl' : 'ltr',
                '&::placeholder': {
                  textAlign: isRTL ? 'right' : 'left',
                  direction: isRTL ? 'rtl' : 'ltr',
                },
              },
            }}
          />
        </Box>
      )}
      <List sx={{ p: 0,
            paddingLeft: '0.5rem !important',
       }}>
        {loading ? (
          <ListItem sx={{ justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
            <ListItemText
              primary="Loading languages..."
              sx={{ 
                textAlign: 'center', 
                color: 'text.secondary',
                ml: 2,
                '& .MuiListItemText-primary': {
                  fontSize: "14px"
                }
              }}
            />
          </ListItem>
        ) : languages.length === 0 ? (
          <ListItem>
            <ListItemText
              primary="No languages available"
              secondary="Failed to load language data"
              sx={{ textAlign: 'center', color: 'text.secondary' }}
            />
          </ListItem>
        ) : filtered.length > 0 ? (
          filtered.map((country, index) => {
            const isActive = country.country_language_code === (currentSelectedItem?.country_language_code || selectedItem?.country_language_code);
            return (
              <ListItem key={`${country.country_language_code}-${index}`} disablePadding>
                <ListItemButton
                  onClick={() => {
                    handleClose(country);
                  }}
                  selected={isActive}
                  sx={{
                    py: { xs: 1.5, sm: 1.75 },
                    px: { xs: 2, sm: 3 },
                    '&.Mui-selected': {
                      backgroundColor: '#f0f8ff',
                      '&:hover': { backgroundColor: '#e3f2fd' },
                    },
                    '&:hover': { backgroundColor: '#f5f5f5' },
                  }}
                >
                  <ListItemText
                    primary={country.samcam_language}
                    sx={{
                      '& .MuiListItemText-primary': {
                        fontSize: { xs: "14px", sm: "16px" },
                        color: "var(--color-text-primary)",
                        fontWeight: isActive ? 500 : 400,
                      }
                    }}
                  />
                  {isActive && (
                    <CheckIcon sx={{ color: '#1976d2', fontSize: { xs: 18, sm: 20 } }} />
                  )}
                </ListItemButton>
              </ListItem>
            );
          })
        ) : (
          <ListItem>
            <ListItemText
              primary="No languages found"
              secondary="Try a different search term"
              sx={{ textAlign: 'center', color: 'text.secondary' }}
            />
          </ListItem>
        )}
      </List>
    </Paper>
  );

  // Landing page variant (original design)
  if (variant === 'landing') {
    return (
      <Box ref={containerRef} sx={{ position: 'relative' }}>
        <TextField
          inputRef={inputRef}
          placeholder={open ? getTranslation("Txt958") || "Search languages..." : getTranslation("Txt1") || "Select language"}
          value={open ? searchTerm : displayLabel}
          onClick={handleTriggerClick}
          onChange={(e) => {
            if (open) {
              setSearchTerm(e.target.value);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              handleClose();
            }
            if (e.key === "Enter" && open && filtered.length > 0) {
              handleClose(filtered[0]).catch(console.error);
            }
          }}
          InputProps={{
            readOnly: !open || isChangingLanguage,
            endAdornment: (
              <InputAdornment position="end">
                <ArrowDropDownIcon />
              </InputAdornment>
            ),
            startAdornment: (
              <InputAdornment position="start">
                <img style={{ marginRight: 8, width: 20, height: 20 }} src={language_btn} alt="Language Icon" />
              </InputAdornment>
            ),
          }}
          sx={{
            backgroundColor: "black",
            color: "white",
            borderRadius: "20px",
            width: { xs: "200px", sm: "250px", md: "280px" },
            cursor: "pointer",
            '& .MuiOutlinedInput-root': {
              color: 'white',
              fontSize: { xs: "12px", sm: "16px" },
              '& fieldset': { borderColor: 'transparent' },
              '&:hover fieldset': { borderColor: 'transparent' },
              '&.Mui-focused fieldset': { borderColor: 'transparent' },
              '& input': {
                color: 'white',
                fontSize: { xs: "12px", sm: "16px" },
                padding: { xs: "8px 12px", sm: "12px 16px" },
                '&::placeholder': {
                  color: 'rgba(255, 255, 255, 0.7)',
                  opacity: 1,
                  fontSize: { xs: "12px", sm: "14px" },
                },
              },
              '& .MuiInputAdornment-root': { color: 'white' },
            },
            '& .MuiInputAdornment-root': { color: 'white' },
          }}
        />
        {open && renderDropdown()}
      </Box>
    );
  }

  // Menu variant (new design)
  return (
    <Box ref={containerRef} sx={{ position: 'relative', width: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        onClick={handleTriggerClick}
        sx={{
          py: 2.5,
          px: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          transition: "background-color 0.2s",
          backgroundColor: open ? "#f5f5f5" : "transparent",
          "&:hover": {
            backgroundColor: "#f5f5f5",
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
          <Box
            sx={{
              // width: 40,
              // height: 40,
              // borderRadius: "50%",
              // // backgroundColor: "#f0f0f0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <LanguageIcon sx={{ color: "var(--color-text-primary)", fontSize: 25 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Box
              sx={{
                fontSize: "0.875rem",
                color: "var(--color-text-secondary)",
                fontWeight: 400,
                mb: 0.5,
              }}
            >
              {getTranslation("Txt957") || "Language"}
            </Box>
            <Box
              sx={{
                fontSize: "1rem",
                color: "var(--color-text-primary)",
                fontWeight: 500,
              }}
            >
              {currentSelectedItem?.samcam_language || currentLanguage?.toUpperCase() || "Spanish"}
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.3s",
            display: "flex",
            alignItems: "center",
          }}
        >
          <ExpandMoreIcon sx={{ color: "var(--color-text-primary)", fontSize: 20 }} />
        </Box>
      </Box>
      {open && renderDropdown()}
    </Box>
  );
};

export default LanguageSelector;
